import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { collection, query, where, getDocs, orderBy, doc, getDoc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { useMarketplace } from '../../context/MarketplaceContext';
import { useCoins } from '../../context/CoinContext';
import { isMarketplaceUser } from '../../utils/roleManager';
import { checkCoinRequirement, MARKETPLACE_FEES } from '../../utils/marketplaceFees';

const BuyerRequestManagement = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { createBuyerRequest } = useMarketplace();
  const { coins } = useCoins();
  const [requests, setRequests] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [requestForm, setRequestForm] = useState({
    productName: '',
    description: '',
    category: '',
    quantity: '',
        unit: 'กรัม',
    desiredPrice: '',
    duration: 7
  });

  useEffect(() => {
    fetchRequests();
  }, [currentUser]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      // ดึงประกาศทั้งหมด (สำหรับผู้ขาย) - ใช้ fallback query ถ้าไม่มี index
      let allRequestsData = [];
      try {
        const allRequestsQuery = query(
          collection(db, 'buyerRequests'),
          where('status', '==', 'active'),
          orderBy('createdAt', 'desc')
        );
        const allRequestsSnapshot = await getDocs(allRequestsQuery);
        allRequestsData = allRequestsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (indexError) {
        console.warn('Index not found for buyerRequests, using fallback query:', indexError);
        const fallbackQuery = query(
          collection(db, 'buyerRequests'),
          where('status', '==', 'active')
        );
        const fallbackSnapshot = await getDocs(fallbackQuery);
        allRequestsData = fallbackSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        // Sort manually
        allRequestsData.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bTime - aTime;
        });
      }
      setRequests(allRequestsData);

      // ดึงประกาศของฉัน (สำหรับผู้ซื้อ) - ใช้ fallback query ถ้าไม่มี index
      if (currentUser) {
        let myRequestsData = [];
        try {
          const myRequestsQuery = query(
            collection(db, 'buyerRequests'),
            where('buyerId', '==', currentUser.uid),
            orderBy('createdAt', 'desc')
          );
          const myRequestsSnapshot = await getDocs(myRequestsQuery);
          myRequestsData = myRequestsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
        } catch (indexError) {
          console.warn('Index not found for myRequests, using fallback query:', indexError);
          const fallbackQuery = query(
            collection(db, 'buyerRequests'),
            where('buyerId', '==', currentUser.uid)
          );
          const fallbackSnapshot = await getDocs(fallbackQuery);
          myRequestsData = fallbackSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          // Sort manually
          myRequestsData.sort((a, b) => {
            const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
            const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
            return bTime - aTime;
          });
        }
        setMyRequests(myRequestsData);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateRequest = async (e) => {
    e.preventDefault();

    if (!isMarketplaceUser(currentUser)) {
      alert(t('buyerRequest.noAccess'));
      return;
    }

    const feeCheck = checkCoinRequirement(coins, 'CREATE_BUYER_REQUEST');
    if (!feeCheck.hasEnough) {
      alert(feeCheck.message);
      return;
    }

    try {
      const endDate = new Date();
      endDate.setDate(endDate.getDate() + parseInt(requestForm.duration));

      await createBuyerRequest({
        ...requestForm,
        quantity: parseFloat(requestForm.quantity),
        desiredPrice: requestForm.desiredPrice ? parseFloat(requestForm.desiredPrice) : null,
        endDate: endDate
      });

      alert(t('buyerRequest.createSuccess'));
      setShowCreateForm(false);
      setRequestForm({
        productName: '',
        description: '',
        category: '',
        quantity: '',
        unit: t('buyerRequest.unit'),
        desiredPrice: '',
        duration: 7
      });
      fetchRequests();
    } catch (error) {
      alert(t('common.saveError') + error.message);
    }
  };

  const handleSubmitOffer = async (requestId) => {
    const price = prompt(t('buyerRequest.enterPrice'));
    if (!price || isNaN(parseFloat(price))) {
      return;
    }

    const message = prompt(t('buyerRequest.additionalMessage')) || '';

    try {
      const requestRef = doc(db, 'buyerRequests', requestId);
      const requestSnap = await getDoc(requestRef);
      
      if (!requestSnap.exists()) {
        throw new Error(t('buyerRequest.requestNotFound'));
      }

      const requestData = requestSnap.data();
      const offers = requestData.offers || [];
      
      offers.push({
        sellerId: currentUser.uid,
        sellerName: currentUser.displayName || currentUser.email,
        storeId: currentUser.storeId || null,
        price: parseFloat(price),
        message: message,
        timestamp: new Date()
      });

      await updateDoc(requestRef, {
        offers,
        updatedAt: new Date()
      });

      alert(t('buyerRequest.offerSuccess'));
      fetchRequests();
    } catch (error) {
      alert(t('common.saveError') + error.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  // ทุกคนใน Marketplace เป็นได้ทั้ง buyer และ seller
  const canAccess = isMarketplaceUser(currentUser);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">{t('buyerRequest.title')}</h1>
          {canAccess && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
            >
              + {t('buyerRequest.createNew')} ({MARKETPLACE_FEES.CREATE_BUYER_REQUEST.default} {t('buyerRequest.coins')})
            </button>
          )}
        </div>

        {showCreateForm && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">{t('buyerRequest.createRequest')}</h2>
            <form onSubmit={handleCreateRequest}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-gray-700 font-semibold mb-2">{t('buyerRequest.productNameLabel')}</label>
                  <input
                    type="text"
                    required
                    value={requestForm.productName}
                    onChange={(e) => setRequestForm({ ...requestForm, productName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">{t('buyerRequest.category')}</label>
                  <select
                    value={requestForm.category}
                    onChange={(e) => setRequestForm({ ...requestForm, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">{t('buyerRequest.selectCategory')}</option>
                    <option value="Flowers">Flowers</option>
                    <option value="Seeds">Seeds</option>
                    <option value="Edibles">Edibles</option>
                    <option value="Pre Rolls">Pre Rolls</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">{t('buyerRequest.quantityLabel')}</label>
                  <input
                    type="number"
                    required
                    min="0"
                    value={requestForm.quantity}
                    onChange={(e) => setRequestForm({ ...requestForm, quantity: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">{t('buyerRequest.unit')}</label>
                  <select
                    value={requestForm.unit}
                    onChange={(e) => setRequestForm({ ...requestForm, unit: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  >
                    <option value={t('buyerRequest.unitGram')}>{t('buyerRequest.unitGram')}</option>
                    <option value={t('buyerRequest.unitKilogram')}>{t('buyerRequest.unitKilogram')}</option>
                    <option value={t('buyerRequest.unitPlant')}>{t('buyerRequest.unitPlant')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">{t('buyerRequest.desiredPriceLabel')}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={requestForm.desiredPrice}
                    onChange={(e) => setRequestForm({ ...requestForm, desiredPrice: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                    placeholder={t('buyerRequest.desiredPricePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-gray-700 font-semibold mb-2">{t('buyerRequest.duration')} *</label>
                  <input
                    type="number"
                    required
                    min="1"
                    max="30"
                    value={requestForm.duration}
                    onChange={(e) => setRequestForm({ ...requestForm, duration: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-gray-700 font-semibold mb-2">{t('buyerRequest.descriptionLabel')}</label>
                <textarea
                  value={requestForm.description}
                  onChange={(e) => setRequestForm({ ...requestForm, description: e.target.value })}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="submit"
                  className="flex-1 bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                >
                  {t('buyerRequest.createButton')} ({MARKETPLACE_FEES.CREATE_BUYER_REQUEST.default} {t('buyerRequest.coins')})
                </button>
                <button
                  type="button"
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
                >
                  {t('common.cancel')}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* My Requests */}
        {canAccess && myRequests.length > 0 && (
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('buyerRequest.myRequests')} ({myRequests.length})</h2>
            <div className="space-y-4">
              {myRequests.map(request => (
                <div key={request.id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">{request.productName}</h3>
                      <p className="text-gray-600 mb-1">{request.description}</p>
                      <p className="text-gray-600">
                        {t('buyerRequest.quantity')}: {request.quantity} {request.unit}
                        {request.desiredPrice && ` | ${t('buyerRequest.desiredPrice')}: ฿${request.desiredPrice.toLocaleString()}`}
                      </p>
                    </div>
                    <div className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      request.status === 'active' ? 'bg-green-100 text-green-800' :
                      request.status === 'fulfilled' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {request.status === 'active' ? 'กำลังเปิด' :
                       request.status === 'fulfilled' ? 'ได้รับข้อเสนอ' :
                       request.status}
                    </div>
                  </div>

                  {request.offers && request.offers.length > 0 && (
                    <div className="mt-4">
                      <h4 className="font-semibold text-gray-700 mb-2">ข้อเสนอ ({request.offers.length})</h4>
                      <div className="space-y-2">
                        {request.offers.map((offer, index) => (
                          <div key={index} className="p-3 bg-gray-50 rounded-lg">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-semibold text-gray-800">{offer.sellerName}</p>
                                {offer.message && <p className="text-sm text-gray-600">{offer.message}</p>}
                              </div>
                              <p className="text-lg font-bold text-green-600">฿{offer.price?.toLocaleString()}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* All Requests (for Sellers) */}
        {canAccess && (
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">{t('buyerRequest.allRequests')} ({requests.length})</h2>
            {requests.length > 0 ? (
              <div className="space-y-4">
                {requests.map(request => (
                  <div key={request.id} className="bg-white rounded-lg shadow-md p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">{request.productName}</h3>
                        <p className="text-gray-600 mb-1">{request.description}</p>
                        <p className="text-gray-600">
                          จำนวน: {request.quantity} {request.unit}
                          {request.desiredPrice && ` | ราคาที่ต้องการ: ฿${request.desiredPrice.toLocaleString()}`}
                        </p>
                        <p className="text-sm text-gray-500 mt-2">
                          ผู้ซื้อ: {request.buyerName} | 
                          วันที่: {new Date(request.createdAt?.toDate()).toLocaleDateString('th-TH')}
                        </p>
                      </div>
                      <div className="px-3 py-1 rounded-full text-sm font-semibold bg-green-100 text-green-800">
                        กำลังเปิด
                      </div>
                    </div>

                    <button
                      onClick={() => handleSubmitOffer(request.id)}
                      className="bg-green-500 hover:bg-green-600 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
                    >
                      {t('buyerRequest.submitOffer')}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <p className="text-gray-500">{t('buyerRequest.noRequests')}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyerRequestManagement;

